import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { productService } from '@/services/productService';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { CategoryTreeSelect } from '../categories/CategoryTreeSelect';
import { useCategories } from '../categories/SubcategoryManager/useCategories';
import { useProductstableTranslations } from '@/hooks/useProductstableTranslations';

interface BulkCategoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: number[];
  onSuccess: () => void;
}

export function BulkCategoryModal({
  open,
  onOpenChange,
  selectedIds,
  onSuccess,
}: BulkCategoryModalProps) {
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const i18n = useProductstableTranslations();

  // Load categories using the hook
  const { categories: categoryTree, isLoading: isCategoriesLoading } = useCategories();

  // Handle category selection
  const handleCategoryChange = (raw: string | number) => {
    const id = typeof raw === 'string' ? +raw : raw;
    setSelectedCategoryId(id === null || id === undefined ? null : id);
  };

  // Handle submit
  const handleSubmit = async () => {
    if (selectedCategoryId === null || selectedIds.length === 0) return;

    setIsSubmitting(true);
    try {
      // Use the bulk category assignment API (send number)
      await productService.bulkAssignCategory(selectedIds, selectedCategoryId as number);

      toast({
        title: i18n.getBulkCategoryModalText('toasts.successTitle', { count: selectedIds.length }),
        description: i18n.getBulkCategoryModalText('toasts.successDesc'),
        variant: 'default',
      });

      onOpenChange(false);
      setSelectedCategoryId(null);
      onSuccess();
    } catch (error) {
      console.error('Error assigning category:', error);
      toast({
        title: i18n.getBulkCategoryModalText('toasts.errorTitle'),
        description: i18n.getBulkCategoryModalText('toasts.errorDesc'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset state when modal closes
  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      setSelectedCategoryId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {i18n.getBulkCategoryModalText('title', { count: selectedIds.length })}
          </DialogTitle>
          <DialogDescription>
            {i18n.getBulkCategoryModalText('description', { count: selectedIds.length })}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">
              {i18n.getBulkCategoryModalText('labels.category')}
            </label>
            <CategoryTreeSelect
              selectedValue={selectedCategoryId}
              onChange={handleCategoryChange}
              className="w-full"
              placeholder={i18n.getBulkCategoryModalText('placeholders.search')}
              disabled={isSubmitting || isCategoriesLoading}
              treeData={categoryTree}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting || isCategoriesLoading}
          >
            {i18n.getBulkCategoryModalText('buttons.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedCategoryId === null || isSubmitting || isCategoriesLoading}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                {i18n.getBulkCategoryModalText('states.assigning')}
              </>
            ) : isCategoriesLoading ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                {i18n.getBulkCategoryModalText('states.loading')}
              </>
            ) : (
              i18n.getBulkCategoryModalText('buttons.apply')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
